import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Search, Download, RefreshCw, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FGInventoryReport = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ totalGroups: 0, totalItems: 0, uniqueBins: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    bin_code: '',
    design_name: '',
    barcode: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchInventory = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.bin_code) params.append('bin_code', filters.bin_code);
      if (filters.design_name) params.append('design_name', filters.design_name);
      if (filters.barcode) params.append('barcode', filters.barcode);
      params.append('limit', '50000');
      
      const response = await apiClient.get(`/wms/fg/put-away/inventory-report?${params.toString()}`);
      
      if (response.data.success) {
        setInventory(response.data.inventory || []);
        setStats({
          totalGroups: response.data.total_groups || 0,
          totalItems: response.data.total_items || 0,
          uniqueBins: response.data.unique_bins || 0
        });
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory report');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchInventory();
  }, []);

  const handleSearch = () => {
    fetchInventory();
  };

  const handleClearFilters = () => {
    setFilters({ bin_code: '', design_name: '', barcode: '' });
  };

  const handleExportCSV = () => {
    if (inventory.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Location Name', 'Bin Status', 'Barcode', 'Design Name', 'Size', 'Shade', 'Description', 'Qty'];
    const csvContent = [
      headers.join(','),
      ...inventory.map(item => [
        item.location_name,
        item.bin_locked ? `"Locked${(item.bin_lock_block_put_away ? ' Put-away' : '') + (item.bin_lock_block_picking ? ' Picking' : '')}"` : 'Open',
        item.barcode,
        `"${item.design_name}"`,
        item.size,
        item.shade,
        `"${item.description || ''}"`,
        item.qty
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `fg_inventory_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  // ==========================================================================
  // USN-wise chunked CSV export (up to 100,000 USNs per file) — respects the
  // page's active filters, exports STORED FG inventory pieces.
  // ==========================================================================
  const [isExportingUSN, setIsExportingUSN] = useState(false);

  const buildUSNExportParams = () => {
    const p = new URLSearchParams();
    if (filters.bin_code) p.append('bin_code', filters.bin_code);
    if (filters.design_name) p.append('design_name', filters.design_name);
    if (filters.barcode) p.append('barcode', filters.barcode);
    return p;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const handleExportUSNsCSV = async () => {
    if (isExportingUSN) return;
    const PART_SIZE = 100000;
    setIsExportingUSN(true);
    try {
      // STEP 1 — ask backend for total USN count so we know how many parts to request
      const countParams = buildUSNExportParams();
      countParams.append('part_size', String(PART_SIZE));
      const countRes = await apiClient.get(`/wms/fg/export-usn-csv/count?${countParams.toString()}`);
      const totalUsns = countRes.data?.total_usns || 0;
      const totalParts = countRes.data?.total_parts || 0;

      if (totalUsns === 0) {
        toast.error('No USNs match the current filters');
        return;
      }

      toast.info(
        totalParts === 1
          ? `Exporting ${totalUsns.toLocaleString()} USN(s) in 1 file…`
          : `Exporting ${totalUsns.toLocaleString()} USN(s) across ${totalParts} file(s)…`
      );

      const dateStr = new Date().toISOString().split('T')[0];

      // STEP 2 — sequentially download each part
      for (let part = 1; part <= totalParts; part += 1) {
        const params = buildUSNExportParams();
        params.append('part', String(part));
        params.append('part_size', String(PART_SIZE));

        // eslint-disable-next-line no-await-in-loop
        const res = await apiClient.get(`/wms/fg/export-usn-csv?${params.toString()}`, {
          responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
        const filename = `fg_inventory_usns_${dateStr}_part${part}-of-${totalParts}.csv`;
        downloadBlob(blob, filename);

        // Small delay so the browser can sequence the save dialogs
        if (part < totalParts) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 350));
        }
      }

      toast.success(
        totalParts === 1
          ? `Exported ${totalUsns.toLocaleString()} USN(s)`
          : `Exported ${totalUsns.toLocaleString()} USN(s) across ${totalParts} files`
      );
    } catch (error) {
      console.error('USN export failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to export USN CSV');
    } finally {
      setIsExportingUSN(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">FG Inventory Report</h1>
                <p className="text-sm text-[#E8DCC8]">View items stored in bins</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportUSNsCSV}
              disabled={isExportingUSN}
              className="bg-amber-500/20 border-amber-400/30 text-amber-100 hover:bg-amber-500/30 disabled:opacity-60"
              data-testid="export-usn-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {isExportingUSN ? 'Exporting…' : 'USN CSV'}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Item Groups</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.totalGroups}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Pieces</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.totalItems}</p>
                </div>
                <Package className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Bins Used</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{stats.uniqueBins}</p>
                </div>
                <Package className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={isLoading}
                  data-testid="search-btn"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Location/Bin Code</label>
                  <Input
                    value={filters.bin_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, bin_code: e.target.value }))}
                    placeholder="e.g., H02-A-01"
                    data-testid="filter-bin-code"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Design Name</label>
                  <Input
                    value={filters.design_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, design_name: e.target.value }))}
                    placeholder="e.g., Omkar"
                    data-testid="filter-design"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Barcode</label>
                  <Input
                    value={filters.barcode}
                    onChange={(e) => setFilters(prev => ({ ...prev, barcode: e.target.value }))}
                    placeholder="e.g., MW15914"
                    data-testid="filter-barcode"
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="ghost" onClick={handleClearFilters} className="w-full">
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Inventory Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : inventory.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No inventory found</p>
                <p className="text-sm text-gray-400">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Location Name</TableHead>
                      <TableHead className="font-semibold">Bin Status</TableHead>
                      <TableHead className="font-semibold">Barcode</TableHead>
                      <TableHead className="font-semibold">Design Name</TableHead>
                      <TableHead className="font-semibold">Size</TableHead>
                      <TableHead className="font-semibold">Shade</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="font-semibold text-right">Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventory.map((item, index) => (
                      <TableRow key={`${item.location_name}-${item.barcode}-${index}`} className="hover:bg-gray-50">
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {item.location_name}
                          </Badge>
                        </TableCell>
                        <TableCell data-testid={`fg-bin-status-${index}`}>
                          {item.bin_locked ? (
                            <Badge className="bg-red-100 text-red-800 border border-red-200">
                              Locked{(() => {
                                const b = [];
                                if (item.bin_lock_block_put_away) b.push('Put-away');
                                if (item.bin_lock_block_picking) b.push('Picking');
                                return b.length ? ` · ${b.join(' + ')}` : '';
                              })()}
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 border border-green-200">Open</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.barcode}</TableCell>
                        <TableCell className="font-medium">{item.design_name}</TableCell>
                        <TableCell>{item.size || '-'}</TableCell>
                        <TableCell>{item.shade || '-'}</TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-gray-600">
                          {item.description || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className="bg-blue-100 text-blue-800">{item.qty}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FGInventoryReport;
